import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Divider,
    Stack,
    Badge,
    IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    SupportAgent as SupportAgentIcon,
    ConfirmationNumber as TicketIcon,
    Notifications as NotificationsIcon,
    Email as EmailIcon,
    Mic as MicIcon,
    Assignment as TaskIcon,
    AutoAwesome as AutoAwesomeIcon,
    Speed as SpeedIcon,
    Security as SecurityIcon,
    Language as LanguageIcon,
    CheckCircle as CheckCircleIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    People as PeopleIcon,
    SmartToy as SmartToyIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    Description as DescriptionIcon,
    Settings as SettingsIcon,
    TrendingUp as TrendingUpIcon,
    Computer as ComputerIcon,
    Cloud as CloudIcon,
    IntegrationInstructions as IntegrationIcon,
    PlayArrow as PlayArrowIcon,
    Pause as PauseIcon,
    VolumeUp as VolumeUpIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const ItHelpdeskAi = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioDuration, setAudioDuration] = useState('00:00');
    const audioRef = useRef(null);

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleAudio = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            const duration = audioRef.current.duration;
            const minutes = Math.floor(duration / 60);
            const seconds = Math.floor(duration % 60);
            setAudioDuration(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        }
    };

    // Ana özellikler
    const mainFeatures = [
        {
            icon: <TicketIcon sx={{ fontSize: 40 }} />,
            title: 'Otomatik Bilet Oluşturma',
            description: 'Kullanıcılardan gelen talepleri anında analiz eder, öncelik seviyesini belirler ve otomatik olarak IT destek bileti oluşturur. Manuel işlem gerektirmez.',
            color: '#1a237e'
        },
        {
            icon: <TaskIcon sx={{ fontSize: 40 }} />,
            title: 'Akıllı Görev Yönetimi',
            description: 'Oluşturulan biletleri doğru ekip üyesine otomatik atar. İlgili kişinin task listesine ekler ve iş yükü dengesini gözetir.',
            color: '#1a237e'
        },
        {
            icon: <NotificationsIcon sx={{ fontSize: 40 }} />,
            title: 'Gerçek Zamanlı Bilgilendirme',
            description: 'Sorunu bildiren kullanıcıya e-posta, SMS veya anlık mesaj ile durum güncellemeleri gönderir. Talep sürecinin her aşamasında kullanıcı bilgilendirilir.',
            color: '#1a237e'
        },
        {
            icon: <PhoneIcon sx={{ fontSize: 40 }} />,
            title: 'Telefon ile Sesli İletişim',
            description: 'Kullanıcılar destek hattını arayarak doğrudan asistanla konuşur. Farklı ses profilleri ile kişiselleştirilebilir. Türkçe ve İngilizce doğal dil işleme ile sorunları anlar ve sesli çözüm sunar.',
            color: '#1a237e'
        },
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
            title: 'Akıllı Çözüm Önerileri',
            description: 'Geçmiş vakaları analiz ederek benzer sorunlara anında çözüm önerileri sunar. Sık karşılaşılan problemleri otomatik çözer.',
            color: '#1a237e'
        },
        {
            icon: <IntegrationIcon sx={{ fontSize: 40 }} />,
            title: 'Entegrasyon Altyapısı',
            description: 'Mevcut IT yönetim sistemlerinize kolayca entegre olur. API desteği ile tüm süreçlerinize adapte olur.',
            color: '#1a237e'
        }
    ];

    // Faydalar
    const benefits = [
        {
            icon: <SpeedIcon />,
            title: '7/24 Anında Destek',
            description: 'Kullanıcılar her an yardım alabilir'
        },
        {
            icon: <ScheduleIcon />,
            title: 'Zaman Tasarrufu',
            description: 'IT ekibiniz stratejik işlere odaklanır'
        },
        {
            icon: <MicIcon />,
            title: 'Özelleştirilebilir Ses',
            description: 'Farklı sesler ve diller ile hizmet'
        },
        {
            icon: <TrendingUpIcon />,
            title: 'Maliyet Azaltma',
            description: 'IT destek maliyetlerini düşürün'
        }
    ];

    // Kullanım senaryoları
    const useCases = [
        {
            icon: <ComputerIcon />,
            title: 'Teknik Sorun Bildirimi',
            description: 'Bilgisayar, yazıcı, ağ sorunları için anında destek talebi oluşturma ve çözüm önerileri sunma.',
            color: '#1a237e'
        },
        {
            icon: <SecurityIcon />,
            title: 'Şifre Sıfırlama',
            description: 'Güvenli kimlik doğrulama ile şifre sıfırlama taleplerini otomatik işleme ve kullanıcıya yönlendirme.',
            color: '#1a237e'
        },
        {
            icon: <SettingsIcon />,
            title: 'Yazılım Talepleri',
            description: 'Yeni yazılım kurulum, güncelleme veya lisans talepleri için onay süreçlerini yönetme.',
            color: '#1a237e'
        },
        {
            icon: <CloudIcon />,
            title: 'Erişim Yönetimi',
            description: 'Sistem, uygulama veya dosya erişim taleplerini güvenli şekilde işleme ve yetkilendirme.',
            color: '#1a237e'
        },
        {
            icon: <PeopleIcon />,
            title: 'Yeni Çalışan Onboarding',
            description: 'Yeni personel için gerekli tüm IT ekipmanı ve hesap oluşturma süreçlerini otomatikleştirme.',
            color: '#1a237e'
        },
        {
            icon: <DescriptionIcon />,
            title: 'Donanım Talepleri',
            description: 'Klavye, mouse, monitör gibi donanım ihtiyaçlarını kaydetme ve tedarik sürecini başlatma.',
            color: '#1a237e'
        }
    ];

    // Neden IT Helpdesk AI?
    const whyItHelpdesk = [
        { icon: <PhoneIcon />, text: 'Telefon Araması – Doğal sesli iletişim' },
        { icon: <MicIcon />, text: 'Özelleştirilebilir Ses – İstediğiniz ses ve dil seçeneği' },
        { icon: <AutoAwesomeIcon />, text: 'Öğrenen Sistem – Her gün daha akıllı' },
        { icon: <IntegrationIcon />, text: 'Kolay Entegrasyon – Mevcut sistemlerinizle uyumlu' },
        { icon: <NotificationsIcon />, text: 'Otomatik Bildirim – E-posta ve SMS entegrasyonu' },
        { icon: <SecurityIcon />, text: 'Güvenli Altyapı – End-to-end şifreleme' }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #0c1136ff 0%, #1a237e 100%)',
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Animated Background */}
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 180, 360],
                        opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    sx={{
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)'
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <SupportAgentIcon sx={{ fontSize: 50, mr: 2 }} />
                                    <Typography variant="h1" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                                        IT Helpdesk AI
                                    </Typography>
                                </Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 300 }}>
                                    Sesli Yapay Zeka IT Destek Asistanı
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.6 }}>
                                    IT destek süreçlerinizi tamamen otomatikleştiren, 7/24 çalışan sesli yapay zeka asistanı.
                                    Kullanıcılar telefon ile arayarak sorunlarını sesli olarak bildirir, asistan anında bilet
                                    oluşturur, görev atar ve çözüm önerilerini sesli olarak iletir.
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        icon={<PhoneIcon />}
                                        label="Telefon Araması"
                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                    />
                                    <Chip
                                        icon={<MicIcon />}
                                        label="Sesli İletişim"
                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                    />
                                    <Chip
                                        icon={<ScheduleIcon />}
                                        label="7/24 Aktif"
                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                    />
                                </Stack>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Paper
                                    elevation={10}
                                    sx={{
                                        borderRadius: 4,
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Call Header */}
                                    <Box sx={{ p: 3, bgcolor: '#1a237e', color: 'white', textAlign: 'center' }}>
                                        <Avatar sx={{ bgcolor: '#fff', color: '#4caf50', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                                            <PhoneIcon sx={{ fontSize: 40 }} />
                                        </Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            IT Helpdesk Sesli Asistan
                                        </Typography>
                                    </Box>

                                    {/* Voice Transcript */}
                                    <Box sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                                        {/* Audio Element */}
                                        <audio
                                            ref={audioRef}
                                            src="/audio/helpdesk-call.wav"
                                            onEnded={handleAudioEnded}
                                            onLoadedMetadata={handleLoadedMetadata}
                                            style={{ display: 'none' }}
                                        />

                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <MicIcon sx={{ color: '#1a237e', mr: 1 }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                                    Örnek Sesli Konuşma Transkripti
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                                onClick={toggleAudio}
                                                sx={{
                                                    bgcolor: '#1a237e',
                                                    '&:hover': { bgcolor: '#0c1136ff' },
                                                    textTransform: 'none',
                                                    borderRadius: 2,
                                                    px: 2
                                                }}
                                                size="small"
                                            >
                                                {isPlaying ? 'Durdur' : 'Sesi Dinle'}
                                            </Button>
                                        </Box>

                                        {/* Asistan Konuşması 1 */}
                                        <Box sx={{ mb: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                <SupportAgentIcon sx={{ fontSize: 18, mr: 1, color: '#1a237e' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                                    AI Asistan
                                                </Typography>
                                            </Box>
                                            <Paper sx={{ p: 1.5, bgcolor: 'white', borderLeft: '3px solid #1a237e' }}>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                    "Merhaba, IT Helpdesk asistanınız. Nasıl yardımcı olabilirim?"
                                                </Typography>
                                            </Paper>
                                        </Box>

                                        {/* Kullanıcı Konuşması */}
                                        <Box sx={{ mb: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Mehmet Yılmaz
                                                </Typography>
                                            </Box>
                                            <Paper sx={{ p: 1.5, bgcolor: 'white', borderLeft: '3px solid #666' }}>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                    "Bilgisayarım açılmıyor, acil yardım lazım."
                                                </Typography>
                                            </Paper>
                                        </Box>

                                        {/* Asistan Yanıtı */}
                                        <Box sx={{ mb: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                <SupportAgentIcon sx={{ fontSize: 18, mr: 1, color: '#1a237e' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                                    AI Asistan
                                                </Typography>
                                            </Box>
                                            <Paper sx={{ p: 1.5, bgcolor: 'white', borderLeft: '3px solid #1a237e' }}>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, fontSize: '0.85rem' }}>
                                                    "Anladım, yüksek öncelikli bilet oluşturdum."
                                                </Typography>

                                                {/* Bilet Bilgisi */}
                                                <Box sx={{ bgcolor: '#e3f2fd', p: 1.5, borderRadius: 2, mb: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <TicketIcon sx={{ fontSize: 16, mr: 1, color: '#1a237e' }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                                            Bilet #HD-2847
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                                        Teknisyen: Ahmet Yılmaz<br />
                                                        E-posta gönderildi<br />
                                                        15 dk içinde aranacaksınız
                                                    </Typography>
                                                </Box>

                                                <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                    "Güç tuşunu 10 saniye basılı tutun, kabloyu çıkarın, 30 saniye bekleyin ve tekrar deneyin."
                                                </Typography>
                                            </Paper>
                                        </Box>

                                        {/* Kullanıcı Teşekkür */}
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Mehmet Yılmaz
                                                </Typography>
                                            </Box>
                                            <Paper sx={{ p: 1.5, bgcolor: 'white', borderLeft: '3px solid #666' }}>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                    "Harika, çok teşekkürler!"
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Ana Özellikler */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<AutoAwesomeIcon />}
                            label="Ana Özellikler"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1a237e' }}>
                            IT Helpdesk AI Ne Yapar?
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                            IT destek süreçlerinizi baştan sona otomatikleştiren akıllı asistan
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {mainFeatures.map((feature, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            minHeight: '200px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-5px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: feature.color,
                                                    width: 70,
                                                    height: 70,
                                                    mr: 3
                                                }}
                                            >
                                                {feature.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: feature.color }}>
                                                    {feature.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                    {feature.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Size Sağlayacağı Faydalar */}
            <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<TrendingUpIcon />}
                            label="Faydalar"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#283593' }}>
                            Size Sağlayacağı Faydalar
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {benefits.map((benefit, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            minHeight: '220px',
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': {
                                                boxShadow: 6,
                                                transform: 'scale(1.05)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: 'primary.main',
                                                width: 70,
                                                height: 70,
                                                mx: 'auto',
                                                mb: 2
                                            }}
                                        >
                                            {benefit.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 600,
                                                minHeight: '56px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {benefit.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.6, flex: 1 }}
                                        >
                                            {benefit.description}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Senaryoları */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<BusinessIcon />}
                            label="Kullanım Senaryoları"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1a237e' }}>
                            Nerelerde Kullanılır?
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                            IT destek süreçlerinizdeki tüm alanlarda aktif rol oynar
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, delay: index * 0.15 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: '180px',
                                            '&:hover': {
                                                boxShadow: 10,
                                                transform: 'translateY(-10px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: useCase.color,
                                                    width: 64,
                                                    height: 64,
                                                    mr: 3
                                                }}
                                            >
                                                {useCase.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: useCase.color, minHeight: '60px', display: 'flex', alignItems: 'center' }}>
                                                    {useCase.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                    {useCase.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Neden IT Helpdesk AI? */}
            <Box sx={{ py: 8, bgcolor: '#1a237e', color: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                            Neden IT Helpdesk AI?
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            IT destek operasyonlarınızı dönüştüren özellikler
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {whyItHelpdesk.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            minHeight: '80px',
                                            height: '100%'
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                width: 50,
                                                height: 50,
                                                mr: 2,
                                                flexShrink: 0
                                            }}
                                        >
                                            {item.icon}
                                        </Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                                            {item.text}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default ItHelpdeskAi;

